import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Activity, Blocks, Building, Building2, FileText, Gauge, History, Landmark, Settings, SlidersHorizontal, Users, UsersRound } from "lucide-react";
import { defineModuleUi, from, unwrap } from "@vixio/data";
import { formatInt, lazyPage } from "@vixio/ui";
import "./rpc-types";
import { IssuersPage } from "./settings/IssuersPage";
import { InfraPanelsPage } from "./settings/InfraPanelsPage";

export { CreateOrganizationForm, slugify } from "./CreateOrganization";
export { PersonForm } from "./PersonForm";
export { PersonPicker } from "./PersonPicker";
export { TAX_REGIMES } from "./settings/IssuersPage";
export type { PersonRow, PublicContacts, SettingDef } from "./rpc-types";
export { usePublicContacts } from "./contacts";
export { INFRA_PANELS } from "./settings/InfraPanelsPage";

// Telas carregadas sob demanda: cada uma vira um arquivo à parte no build.
const PeoplePage = lazyPage(() => import("./PeoplePage"), "PeoplePage");
const PersonPage = lazyPage(() => import("./PersonPage"), "PersonPage");
const OrganizationsPage = lazyPage(() => import("./OrganizationsPage"), "OrganizationsPage");
const OrgSettingsPage = lazyPage(() => import("./settings/OrgSettingsPage"), "OrgSettingsPage");
const MembersPage = lazyPage(() => import("./settings/MembersPage"), "MembersPage");
const ModulesPage = lazyPage(() => import("./settings/ModulesPage"), "ModulesPage");
const AuditPage = lazyPage(() => import("./settings/AuditPage"), "AuditPage");
const EventsPage = lazyPage(() => import("./settings/EventsPage"), "EventsPage");
const GeneralSettingsPage = lazyPage(() => import("./settings/GeneralSettingsPage"), "GeneralSettingsPage");
const TermsPage = lazyPage(() => import("./settings/TermsPage"), "TermsPage");

function EventHealthWidget() {
  const q = useQuery({
    queryKey: ["core", "event_health", "widget"],
    queryFn: () => unwrap<Array<{ pending: number; dead: number }>>(from("core", "v_event_health").select("pending, dead")),
  });
  const dead = (q.data ?? []).reduce((a, r) => a + r.dead, 0);
  const pending = (q.data ?? []).reduce((a, r) => a + r.pending, 0);
  return (
    <div className="vx-stack">
      <p>
        <span className="num" style={{ fontSize: "var(--fs-20)", fontWeight: 600, color: dead > 0 ? "var(--crit)" : undefined }}>
          {formatInt(dead)}
        </span>{" "}
        <span className="text-2">na fila de erro</span>
      </p>
      <p className="text-2">
        <span className="num">{formatInt(pending)}</span> entrega(s) pendente(s).{" "}
        <Link to="/configuracoes/eventos">Ver fila</Link>
      </p>
    </div>
  );
}

export default defineModuleUi({
  key: "core",
  title: "Núcleo",
  domain: "platform",
  icon: Settings,
  menu: [
    { path: "/pessoas", label: "Pessoas", minRole: "member", icon: Users, domain: "relationship", order: 10, keywords: "contatos clientes cadastro lgpd" },
    { path: "/configuracoes/organizacao", label: "Organização", minRole: "member", icon: Building2, order: 10 },
    { path: "/configuracoes/gerais", label: "Gerais", minRole: "member", icon: SlidersHorizontal, order: 11, keywords: "parâmetros e-mail contato privacidade site fuso remetente emissor padrão alerta" },
    { path: "/configuracoes/termos", label: "Termos LGPD", minRole: "member", icon: FileText, order: 12, keywords: "consentimento privacidade termo versão finalidade" },
    { path: "/configuracoes/emissores", label: "Emissores (CNPJs)", minRole: "member", icon: Landmark, order: 20, keywords: "cnpj regime tributário" },
    { path: "/configuracoes/membros", label: "Membros e convites", minRole: "member", icon: UsersRound, order: 30, keywords: "usuários papéis convite" },
    { path: "/configuracoes/modulos", label: "Módulos", minRole: "member", icon: Blocks, order: 40, keywords: "feature flag ligar desligar" },
    { path: "/configuracoes/auditoria", label: "Auditoria", minRole: "admin", icon: History, order: 50 },
    { path: "/configuracoes/eventos", label: "Fila de eventos", minRole: "admin", icon: Activity, order: 60, keywords: "outbox dlq entregas webhook" },
    { path: "/configuracoes/infraestrutura", label: "Painéis de infraestrutura", minRole: "admin", icon: Gauge, order: 65, keywords: "status uptime kuma monitor analytics umami estatísticas portão" },
    { path: "/organizacoes", label: "Organizações", minRole: "viewer", icon: Building, order: 70, platformAdmin: true },
  ],
  routes: [
    { path: "/pessoas", element: <PeoplePage />, minRole: "member" },
    { path: "/pessoas/:id", element: <PersonPage />, minRole: "member" },
    { path: "/organizacoes", element: <OrganizationsPage />, minRole: "viewer" },
    { path: "/configuracoes/organizacao", element: <OrgSettingsPage />, minRole: "member" },
    { path: "/configuracoes/gerais", element: <GeneralSettingsPage />, minRole: "member" },
    { path: "/configuracoes/termos", element: <TermsPage />, minRole: "member" },
    { path: "/configuracoes/emissores", element: <IssuersPage />, minRole: "member" },
    { path: "/configuracoes/membros", element: <MembersPage />, minRole: "member" },
    { path: "/configuracoes/modulos", element: <ModulesPage />, minRole: "member" },
    { path: "/configuracoes/auditoria", element: <AuditPage />, minRole: "admin" },
    { path: "/configuracoes/eventos", element: <EventsPage />, minRole: "admin" },
    { path: "/configuracoes/infraestrutura", element: <InfraPanelsPage />, minRole: "admin" },
  ],
  widgets: [{ key: "core.events", title: "Fila de eventos", minRole: "admin", span: 1, order: 90, element: EventHealthWidget }],
});
